// dashboard.jsx — Unified Dashboard (FIXED)
// Fix: getCurrrentUser() is now guarded behind !isCreator to prevent
//      the GET /api/users/userinfo/{id} fetch for creator sessions.
//      That endpoint queries payouts by user_id which references
//      balance_credited_at — a column that doesn't exist in the DB yet.
//
// DESKTOP (md+): Financial page left  +  Social sidebar right
//   • Sidebar can be minimized to a 48px icon strip
//   • Full-screen feed dialog: premium two-column layout
//   • No avatar in composer, no label on For You tab
// MOBILE  (<md):  ZapFeed / Bubbles  (original mobile experience)

import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Box, Typography, useMediaQuery, Snackbar, Alert, CircularProgress,
  Avatar, Chip, IconButton, Button, Badge, Tabs, Tab, Skeleton,
  TextField, Tooltip, Dialog, Card, Paper, Divider,
} from "@mui/material";
import { styled, alpha, keyframes, useTheme } from "@mui/material/styles";
import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Groups as GroupsIcon,
  Explore as ExploreIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Campaign as CampaignIcon,
  OpenInFull as ExpandIcon,
  AutoAwesome as SparkleIcon,
  ArrowBack as BackIcon,
  EmojiEvents as TrophyIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  Bolt as BoltIcon,
  People as PeopleIcon,
} from "@mui/icons-material";

import useAuth from "../../../Hooks/use-auth";
import { useCreatorAuth } from "../../../Hooks/creator-use-auth";
import API from "../../../API";
import { queryKeys } from "../../../API/queryKeys";

import FeedItem from "../../../CreatorUser/CreatorPages/Dashboard/zapfeed/components/unitedfeed";
import ThreadShareDialog from "../../../CreatorUser/CreatorPages/Dashboard/zapfeed/components/ThreadShareDialog";
import ZapFeed from "../../../CreatorUser/CreatorPages/Dashboard/zapfeed/zapfeed";
import BubbleFeed from "../../../Bubbles/BubbleFeed";
import CastingCallDialog from "../Campaigns/castingcalls/castingcalldialog";

import AchievementCelebrationDialog from "./blitzboltpopup";
import CreateCallDialog from "../Campaigns/castingcalls/CreateCallDialog";
import UploadFileDialog from "../../../Components/tools/uploadfile";
import BlitzPay from "../../BlitzPay/blitzpay";
import PayoutsInvoicing from "../../../CreatorUser/CreatorPages/Dashboard/payoutsinvoicing";

/* ─────────────────────────────────────────────────────────
   Constants & keyframes
───────────────────────────────────────────────────────── */
const IDLE_MS = 34_000;
const ROW_COUNT = 20;
const SIDEBAR_FULL_WIDTH = 340;
const SIDEBAR_MINI_WIDTH = 48;

const idleFill = keyframes`
  from { width: 0%; }
  to   { width: 100%; }
`;
const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

/* ─────────────────────────────────────────────────────────
   Desktop layout shells
───────────────────────────────────────────────────────── */
const PageContainer = styled(Box)(() => ({
  display: "flex",
  minHeight: "100vh",
  width: "100%",
  alignItems: "flex-start",
}));

const FinancialColumn = styled(Box)(() => ({
  flex: "1 1 0",
  minWidth: 0,
  overflowX: "hidden",
  transition: "width 0.25s ease",
}));

const SidebarColumn = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  borderLeft: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
  transition: "width 0.25s ease",
  overflow: "hidden",
}));

const StickyWrapper = styled(Box)(() => ({
  position: "sticky",
  top: 0,
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  position: "relative",
  overflow: "hidden",
  background: alpha(theme.palette.background.default, 0.97),
  backdropFilter: "blur(16px)",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
}));

const FeedScroll = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "thin",
  scrollbarColor: `${alpha(theme.palette.text.primary, 0.1)} transparent`,
  "&::-webkit-scrollbar": { width: 3 },
  "&::-webkit-scrollbar-thumb": { borderRadius: 4, background: alpha(theme.palette.text.primary, 0.1) },
}));

// Icon-only tab — no label for For You
const IconTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  minHeight: 44,
  minWidth: 44,
  padding: theme.spacing(0, 1.5),
  "&.Mui-selected": { color: theme.palette.primary.main },
}));

// Tab with label for Bubbles
const LabelTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8rem",
  minHeight: 44,
  flex: 1,
  "&.Mui-selected": { color: theme.palette.primary.main },
}));

// Composer — no avatar, clean textarea
const ComposerBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25, 1.5),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.38)}`,
  background: alpha(theme.palette.background.paper, 0.6),
}));

const FeaturedCard = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1, 1.25, 0),
  padding: theme.spacing(1.1, 1.25),
  borderRadius: theme.spacing(1.75),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
  background: `linear-gradient(135deg,
    ${alpha(theme.palette.primary.main, 0.07)} 0%,
    ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
  animation: `${slideDown} 0.2s ease`,
}));

const AutoBanner = styled(Box)(({ theme }) => ({
  display: "flex", alignItems: "center", justifyContent: "space-between",
  gap: theme.spacing(1),
  padding: theme.spacing(0.7, 1.5),
  background: `linear-gradient(90deg,
    ${alpha(theme.palette.primary.main, 0.09)} 0%,
    ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
  animation: `${slideDown} 0.22s ease`,
  flexShrink: 0,
}));

const MiniStrip = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  width: SIDEBAR_MINI_WIDTH,
  height: "100%",
  background: alpha(theme.palette.background.default, 0.97),
  backdropFilter: "blur(16px)",
}));

/* ─────────────────────────────────────────────────────────
   Full-Screen Feed Dialog styled components
───────────────────────────────────────────────────────── */
const FeedDialogHeader = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  position: "relative",
  overflow: "hidden",
  background: `linear-gradient(135deg,
    ${theme.palette.primary.dark} 0%,
    ${theme.palette.primary.main} 40%,
    ${theme.palette.secondary.main} 100%)`,
  padding: theme.spacing(2.5, 3),
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: `linear-gradient(90deg,
      transparent 0%,
      ${alpha("#fff", 0.06)} 50%,
      transparent 100%)`,
    backgroundSize: "200% 100%",
    animation: `${shimmer} 4s linear infinite`,
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -60,
    right: -40,
    width: 220,
    height: 220,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.35)} 0%, transparent 70%)`,
    pointerEvents: "none",
  },
}));

const DialogFeedCol = styled(Box)(({ theme }) => ({
  flex: "1 1 0",
  minWidth: 0,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "thin",
  scrollbarColor: `${alpha(theme.palette.text.primary, 0.1)} transparent`,
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": { borderRadius: 4, background: alpha(theme.palette.text.primary, 0.1) },
}));

const DialogSidePanel = styled(Box)(({ theme }) => ({
  width: 300,
  flexShrink: 0,
  borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(2.5),
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  [theme.breakpoints.down("lg")]: { display: "none" },
}));

const SidePanelCard = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
  boxShadow: "none",
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(8px)",
}));

const StatRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 0),
  "&:not(:last-child)": {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
  },
}));

/* ─────────────────────────────────────────────────────────
   Mobile layout shells
───────────────────────────────────────────────────────── */
const MobileContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  paddingTop: theme.spacing(2),
}));

const MobileTabBar = styled(Card)(({ theme }) => ({
  position: "sticky",
  top: theme.spacing(1),
  zIndex: 100,
  marginBottom: theme.spacing(2),
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  borderRadius: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: "blur(20px)",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  overflow: "hidden",
}));

const MobileTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.95rem",
  minHeight: 52,
  "&.Mui-selected": { color: theme.palette.primary.main, fontWeight: 700 },
}));

/* ─────────────────────────────────────────────────────────
   Shared helpers
───────────────────────────────────────────────────────── */
const SectionDivider = ({ label }) => {
  const theme = useTheme();
  return (
    <Box sx={{ px: 1.5, py: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
      <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.9, color: "text.disabled", whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: "1px", bgcolor: alpha(theme.palette.divider, 0.35) }} />
    </Box>
  );
};

function useSidebarReferral() {
  const [referralLink, setReferralLink] = useState("");
  const { isAuthorized: userOK } = useAuth();
  const { isAuthorized: creatorOK } = useCreatorAuth();
  const { mutate: genLink, isLoading } = useMutation(
    () => API.globalUsers.generateInviteGen(),
    { onSuccess: (d) => { if (d?.invite_url) setReferralLink(d.invite_url); } }
  );
  useEffect(() => {
    if ((userOK || creatorOK) && !referralLink) genLink();
  }, [userOK, creatorOK]);
  const copyLink = useCallback(async () => {
    if (!referralLink) return false;
    try { await navigator.clipboard.writeText(referralLink); return true; }
    catch { return false; }
  }, [referralLink]);
  return { referralLink, isLoading, copyLink };
}

/* ─────────────────────────────────────────────────────────
   SidebarFeatured
───────────────────────────────────────────────────────── */
function SidebarFeatured({ onCastingCallCreate, referralLink, onCopyLink, isGenerating, onDismiss }) {
  const theme = useTheme();
  const [variant] = useState(() => Math.random() < 0.5 ? "referral" : "casting");
  const [snack, setSnack] = useState(null);

  const handleCopy = async () => {
    const ok = await onCopyLink();
    setSnack({ msg: ok ? "Referral link copied!" : "Failed to copy", sev: ok ? "success" : "error" });
  };

  return (
    <>
      <FeaturedCard>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{
            width: 34, height: 34, flexShrink: 0, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: variant === "referral"
              ? "linear-gradient(135deg, #4CAF50, #66BB6A)"
              : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            boxShadow: variant === "referral"
              ? `0 2px 8px ${alpha("#4CAF50", 0.35)}`
              : `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
          }}>
            {variant === "referral"
              ? <TrophyIcon sx={{ color: "white", fontSize: 17 }} />
              : <CampaignIcon sx={{ color: "white", fontSize: 17 }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.3 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.75rem", color: variant === "referral" ? "#4CAF50" : "primary.main" }}>
                {variant === "referral" ? "Earn Referral Rewards" : "Post a Casting Call"}
              </Typography>
              <IconButton size="small" onClick={onDismiss} sx={{ p: 0.2, opacity: 0.45, "&:hover": { opacity: 1 } }}>
                <CloseIcon sx={{ fontSize: 11 }} />
              </IconButton>
            </Box>
            <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", lineHeight: 1.4, mb: 0.75 }}>
              {variant === "referral"
                ? "Invite friends and earn Blitz Points + cash"
                : "Find creators for your next campaign"}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75 }}>
              {variant === "referral" ? (
                <>
                  <Button size="small" variant="contained"
                    startIcon={isGenerating ? <CircularProgress size={10} sx={{ color: "white" }} /> : <CopyIcon sx={{ fontSize: 12 }} />}
                    onClick={handleCopy} disabled={!referralLink || isGenerating}
                    sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: "0.65rem", px: 1.5, py: 0.3, bgcolor: "#4CAF50", "&:hover": { bgcolor: "#45a049" } }}>
                    {isGenerating ? "Generating…" : "Copy Link"}
                  </Button>
                  <Button size="small" variant="outlined"
                    startIcon={<ViewIcon sx={{ fontSize: 12 }} />} href="/my-referrals"
                    sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: "0.65rem", px: 1.5, py: 0.3, borderColor: "#4CAF50", color: "#4CAF50", "&:hover": { bgcolor: alpha("#4CAF50", 0.08) } }}>
                    Stats
                  </Button>
                </>
              ) : (
                <Button size="small" variant="contained"
                  startIcon={<CampaignIcon sx={{ fontSize: 12 }} />} onClick={onCastingCallCreate}
                  sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: "0.65rem", px: 1.5, py: 0.3 }}>
                  Create Call
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </FeaturedCard>
      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {snack && <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)}>{snack.msg}</Alert>}
      </Snackbar>
    </>
  );
}

/* ─────────────────────────────────────────────────────────
   Full-Screen Feed Dialog — premium two-column layout
───────────────────────────────────────────────────────── */
function FullFeedDialog({ open, onClose, onCastingCallCreate, referralLink, onCopyReferralLink, onViewReferrals, isGeneratingReferral, displayName, bubbleCount, photo }) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("lg"));
  const [snack, setSnack] = useState(null);

  const handleCopyRef = async () => {
    const ok = await onCopyReferralLink();
    if (ok !== false) setSnack({ msg: "Referral link copied!", sev: "success" });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{ zIndex: theme.zIndex.modal }}
      PaperProps={{
        sx: {
          m: 0,
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
          overflow: "hidden",
        },
      }}
    >
      <FeedDialogHeader>
        <Box sx={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2.5, flexShrink: 0,
              background: alpha("#fff", 0.15),
              border: `1px solid ${alpha("#fff", 0.25)}`,
              backdropFilter: "blur(8px)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <BoltIcon sx={{ color: "white", fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: "white", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                For You
              </Typography>
              <Typography sx={{ fontSize: "0.72rem", color: alpha("#fff", 0.7), fontWeight: 500, mt: 0.2 }}>
                Your personalized Blitz feed
              </Typography>
            </Box>
            {!isSm && (
              <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                <Chip
                  icon={<PeopleIcon sx={{ fontSize: 13, color: "white !important" }} />}
                  label={`${bubbleCount} Bubbles`}
                  size="small"
                  sx={{ height: 24, fontSize: "0.68rem", fontWeight: 600, bgcolor: alpha("#fff", 0.15), color: "white", border: `1px solid ${alpha("#fff", 0.2)}`, "& .MuiChip-icon": { color: "white" } }}
                />
                <Chip
                  icon={<TrendingUpIcon sx={{ fontSize: 13, color: "white !important" }} />}
                  label="Live feed"
                  size="small"
                  sx={{ height: 24, fontSize: "0.68rem", fontWeight: 600, bgcolor: alpha("#fff", 0.15), color: "white", border: `1px solid ${alpha("#fff", 0.2)}`, "& .MuiChip-icon": { color: "white" } }}
                />
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {!isSm && (
              <Button
                variant="outlined" size="small" onClick={onCastingCallCreate}
                startIcon={<CampaignIcon sx={{ fontSize: 15 }} />}
                sx={{ color: "white", borderColor: alpha("#fff", 0.4), textTransform: "none", fontWeight: 600, fontSize: "0.78rem", borderRadius: 20, px: 2, "&:hover": { borderColor: "white", bgcolor: alpha("#fff", 0.1) } }}
              >
                Casting Call
              </Button>
            )}
            <Tooltip title="Close full feed (Esc)">
              <IconButton onClick={onClose} sx={{ color: "white", bgcolor: alpha("#fff", 0.12), border: `1px solid ${alpha("#fff", 0.2)}`, "&:hover": { bgcolor: alpha("#fff", 0.22) } }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </FeedDialogHeader>

      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", animation: `${fadeIn} 0.2s ease` }}>
        <DialogFeedCol>
          <ZapFeed
            onCastingCallCreate={onCastingCallCreate}
            referralLink={referralLink}
            onCopyReferralLink={onCopyReferralLink}
            onViewReferrals={onViewReferrals}
            isGeneratingReferral={isGeneratingReferral}
          />
        </DialogFeedCol>

        <DialogSidePanel>
          <SidePanelCard sx={{ background: `linear-gradient(135deg, ${alpha("#4CAF50", 0.07)} 0%, ${alpha("#66BB6A", 0.12)} 100%)`, border: `1px solid ${alpha("#4CAF50", 0.22)}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg, #4CAF50, #66BB6A)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 3px 10px ${alpha("#4CAF50", 0.4)}` }}>
                <TrophyIcon sx={{ color: "white", fontSize: 19 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#4CAF50", lineHeight: 1.2 }}>Referral Rewards</Typography>
                <Typography sx={{ fontSize: "0.65rem", color: "text.secondary" }}>Earn Blitz Points + cash</Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.5, mb: 1.25 }}>
              Share your link and earn rewards every time a friend joins Blitz.
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" fullWidth variant="contained"
                startIcon={isGeneratingReferral ? <CircularProgress size={11} sx={{ color: "white" }} /> : <CopyIcon sx={{ fontSize: 13 }} />}
                onClick={handleCopyRef} disabled={!referralLink || isGeneratingReferral}
                sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: "0.72rem", bgcolor: "#4CAF50", "&:hover": { bgcolor: "#45a049" }, py: 0.75 }}>
                {isGeneratingReferral ? "Generating…" : "Copy Link"}
              </Button>
              <Button size="small" variant="outlined" startIcon={<ViewIcon sx={{ fontSize: 13 }} />} onClick={onViewReferrals}
                sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: "0.72rem", borderColor: "#4CAF50", color: "#4CAF50", px: 1.5, py: 0.75, flexShrink: 0, "&:hover": { bgcolor: alpha("#4CAF50", 0.08) } }}>
                Stats
              </Button>
            </Box>
          </SidePanelCard>

          <SidePanelCard sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.35)}` }}>
                <CampaignIcon sx={{ color: "white", fontSize: 19 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "primary.main", lineHeight: 1.2 }}>Post a Casting Call</Typography>
                <Typography sx={{ fontSize: "0.65rem", color: "text.secondary" }}>Find your next creator</Typography>
              </Box>
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.5, mb: 1.25 }}>
              Reach thousands of creators for UGC, influencer posts, and more.
            </Typography>
            <Button size="small" fullWidth variant="contained" startIcon={<CampaignIcon sx={{ fontSize: 13 }} />} onClick={onCastingCallCreate}
              sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: "0.72rem", py: 0.75 }}>
              Create Casting Call
            </Button>
          </SidePanelCard>

          <SidePanelCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
              <GroupsIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.82rem" }}>Your Bubbles</Typography>
              {bubbleCount > 0 && <Chip label={bubbleCount} size="small" color="primary" sx={{ height: 18, fontSize: "0.62rem", ml: "auto" }} />}
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.5, mb: 1.25 }}>
              {bubbleCount > 0
                ? `You're part of ${bubbleCount} communit${bubbleCount === 1 ? "y" : "ies"}. Jump in and connect.`
                : "Join communities of creators and collaborators."}
            </Typography>
            <Button size="small" fullWidth variant="outlined" startIcon={<GroupsIcon sx={{ fontSize: 13 }} />} href="/?tab=bubbles"
              sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: "0.72rem", py: 0.75 }}>
              {bubbleCount > 0 ? "Go to Bubbles" : "Explore Communities"}
            </Button>
          </SidePanelCard>

          <SidePanelCard>
            <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", mb: 1.25 }}>Feed Tips</Typography>
            {[
              { icon: <BoltIcon sx={{ fontSize: 15, color: "warning.main" }} />, text: "React to posts to boost their visibility" },
              { icon: <CampaignIcon sx={{ fontSize: 15, color: "secondary.main" }} />, text: "Share casting calls to attract talent fast" },
              { icon: <TrophyIcon sx={{ fontSize: 15, color: "#4CAF50" }} />, text: "Earn Blitz Points for every interaction" },
            ].map(({ icon, text }, i) => (
              <StatRow key={i}>
                <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "action.hover", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</Box>
                <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.4 }}>{text}</Typography>
              </StatRow>
            ))}
          </SidePanelCard>
        </DialogSidePanel>
      </Box>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {snack && <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)}>{snack.msg}</Alert>}
      </Snackbar>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────
   Desktop Social Sidebar
───────────────────────────────────────────────────────── */
function SocialSidebar({ onCastingCallCreate, minimized, onToggleMinimize }) {
  const theme = useTheme();
  const qc = useQueryClient();
  const { creatorToken, isAuthorized: creatorOK } = useCreatorAuth();
  const { isAuthorized: userOK, getCurrrentUser } = useAuth();

  const isCreator = !!creatorToken?.creator_user;

  // ── FIXED: only call getCurrrentUser() for non-creator sessions.
  // For creators, getCurrrentUser() triggers GET /api/users/userinfo/{id}
  // which queries the payouts table by user_id — hitting the missing
  // balance_credited_at column and returning a 500.
  let displayName = "Me";
  let photo;
  if (isCreator) {
    const c = creatorToken.creator_user;
    displayName = c.creator?.display_name || c.username || displayName;
    photo = c.creator?.pfphref || c.pfphref;
  } else if (userOK) {
    const u = getCurrrentUser(); // only called for non-creator users
    displayName = u?.first_name || u?.username || displayName;
    photo = u?.phphref;
  }

  const { data: bubblesData } = useQuery(["bubbles-list"], () => API.bubbles.list(), { enabled: userOK || creatorOK, staleTime: 60_000, refetchOnWindowFocus: false });
  const bubbleCount = bubblesData?.bubbles?.length || 0;
  const { referralLink, isLoading: isGenRef, copyLink } = useSidebarReferral();

  const [activeTab, setActiveTab] = useState(0);
  const [wasAutoSwitched, setWasAutoSwitched] = useState(false);
  const [idleKey, setIdleKey] = useState(0);
  const idleTimerRef = useRef(null);

  const scheduleIdleSwitch = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      setActiveTab((cur) => { if (cur === 0) { setWasAutoSwitched(true); return 1; } return cur; });
    }, IDLE_MS);
  }, []);

  const handleInteraction = useCallback(() => {
    setWasAutoSwitched((was) => { if (was) setActiveTab(0); return false; });
    setIdleKey((k) => k + 1);
    scheduleIdleSwitch();
  }, [scheduleIdleSwitch]);

  useEffect(() => { scheduleIdleSwitch(); return () => clearTimeout(idleTimerRef.current); }, [activeTab, scheduleIdleSwitch]);

  const [text, setText] = useState("");
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [snack, setSnack] = useState(null);
  const [showFeatured, setShowFeatured] = useState(true);
  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [castingCallViewOpen, setCastingCallViewOpen] = useState(false);
  const [selectedCastingCall, setSelectedCastingCall] = useState(null);
  const [castingCallPosterInfo, setCastingCallPosterInfo] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareActivity, setShareActivity] = useState(null);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage, refetch } =
    useInfiniteQuery(
      queryKeys.creator.zapfeed({ rows: ROW_COUNT, show_replies: false }),
      ({ pageParam = 0 }) => API.zapfeed.list({ page: pageParam, rows: ROW_COUNT, show_replies: false }),
      { getNextPageParam: (last, pages) => last.zapfeed.length === ROW_COUNT ? pages.length : undefined, staleTime: 45_000, refetchOnWindowFocus: false }
    );

  useEffect(() => { if (userOK || creatorOK) refetch(); }, [userOK, creatorOK]);

  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && fetchNextPage(), { rootMargin: "200px 0px 0px 0px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [fetchNextPage, hasNextPage]);

  const feed = useMemo(() =>
    data?.pages.flatMap((p) => p.zapfeed.filter((i) => i.activity_type !== "Reply").map((item) => ({
      ...item,
      is_global: !item.bubble_id && item.is_global !== false,
      bubble_name: item.bubble_name || null,
    }))) ?? [],
    [data]
  );

  const createPost = useMutation(
    async ({ text, mediaData }) => {
      const d = { text: text || "" };
      if (mediaData) { d.media_url = mediaData.url; d.media_type = mediaData.type; }
      return API.zapfeed.post(d);
    },
    {
      onSuccess: ({ post }) => {
        const newItem = {
          activity_id: post.id, activity_name: post.text, activity_type: "Post",
          activity_date: post.created_at,
          creator_id: isCreator ? creatorToken.creator_user.username : null,
          user_id: null, // never set user_id for creator sessions — avoids leaking user identity
          pfphref: photo, display_name: displayName,
          reaction_counts: {}, total_reactions: 0, user_reaction: null,
          can_react: true, reply_count: 0,
          media_url: post.media_url, media_type: post.media_type, is_global: true,
        };
        const qk = queryKeys.creator.zapfeed({ rows: ROW_COUNT, show_replies: false });
        qc.setQueryData(qk, (old) => {
          if (!old) return old;
          return { ...old, pages: [{ ...old.pages[0], zapfeed: [newItem, ...old.pages[0].zapfeed] }, ...old.pages.slice(1)] };
        });
        qc.invalidateQueries(queryKeys.creator.zapfeed());
        setText(""); setUploadedMedia(null);
        setSnack({ msg: "Posted!", sev: "success" });
      },
      onError: (err) => {
        const msg = err?.response?.data?.error || "";
        const is429 = err?.response?.status === 409 || msg.toLowerCase().includes("once per day");
        setSnack({ msg: is429 ? "One post per day max." : (msg || "Something went wrong."), sev: is429 ? "warning" : "error" });
      },
    }
  );

  const handleCastingCallClick = useCallback((activity) => {
    if (activity.casting_call) {
      setSelectedCastingCall(activity.casting_call);
      setCastingCallPosterInfo({ pfphref: activity.pfphref, display_name: activity.display_name });
      setCastingCallViewOpen(true);
    }
  }, []);

  const handleCastingCallShareClick = useCallback((e, activity) => {
    e.stopPropagation();
    if (activity?.casting_call?.public_slug) {
      navigator.clipboard.writeText(`${window.location.origin}/casting-calls/public/${activity.casting_call.public_slug}`)
        .then(() => setSnack({ msg: "Link copied!", sev: "success" }))
        .catch(() => setSnack({ msg: "Failed to copy", sev: "error" }));
    }
  }, []);

  const handleShare = useCallback((activity) => { setShareActivity(activity); setShareDialogOpen(true); }, []);

  const handlePost = () => {
    const t = text.trim();
    if ((!t && !uploadedMedia) || t.length > 280 || createPost.isLoading) return;
    createPost.mutate({ text: t, mediaData: uploadedMedia });
  };

  /* ── MINIMIZED STRIP ── */
  if (minimized) {
    return (
      <StickyWrapper>
        <MiniStrip>
          <Tooltip title="Expand feed" placement="left">
            <IconButton size="small" onClick={onToggleMinimize}
              sx={{ color: "text.secondary", bgcolor: alpha(theme.palette.primary.main, 0.08), "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.16), color: "primary.main" } }}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="For You" placement="left">
            <IconButton size="small" onClick={() => { onToggleMinimize(); setActiveTab(0); }}
              sx={{ color: activeTab === 0 ? "primary.main" : "text.secondary" }}>
              <ExploreIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bubbles" placement="left">
            <IconButton size="small" onClick={() => { onToggleMinimize(); setActiveTab(1); }}
              sx={{ color: activeTab === 1 ? "primary.main" : "text.secondary" }}>
              <Badge color="primary" variant={bubbleCount > 0 ? "dot" : undefined} invisible={bubbleCount <= 0}
                sx={{ "& .MuiBadge-badge": { right: -1, top: 1, minWidth: 7, height: 7 } }}>
                <GroupsIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Open full feed" placement="left">
            <IconButton size="small" onClick={() => setFeedDialogOpen(true)}
              sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}>
              <ExpandIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </MiniStrip>

        <FullFeedDialog
          open={feedDialogOpen}
          onClose={() => setFeedDialogOpen(false)}
          onCastingCallCreate={onCastingCallCreate}
          referralLink={referralLink}
          onCopyReferralLink={copyLink}
          onViewReferrals={() => window.location.assign("/my-referrals")}
          isGeneratingReferral={isGenRef}
          displayName={displayName}
          bubbleCount={bubbleCount}
          photo={photo}
        />
      </StickyWrapper>
    );
  }

  /* ── FULL SIDEBAR ── */
  return (
    <StickyWrapper onMouseMove={handleInteraction} onClick={handleInteraction} onKeyDown={handleInteraction}>
      <SidebarHeader>
        <Box sx={{ display: "flex", alignItems: "center", pr: 0.5 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => { setActiveTab(v); setWasAutoSwitched(false); }}
            sx={{
              flex: 1, minHeight: 44,
              "& .MuiTabs-indicator": { height: 2.5, borderRadius: "2px 2px 0 0", background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` },
              "& .MuiTabs-flexContainer": { justifyContent: "flex-start" },
            }}
          >
            <IconTab icon={<ExploreIcon sx={{ fontSize: 18 }} />} aria-label="For You" />
            <LabelTab
              icon={
                <Badge color="primary" variant={bubbleCount > 0 ? "dot" : undefined} invisible={bubbleCount <= 0}
                  sx={{ "& .MuiBadge-badge": { right: -2, top: 3, minWidth: 7, height: 7, borderRadius: "50%" } }}>
                  <GroupsIcon sx={{ fontSize: 15 }} />
                </Badge>
              }
              iconPosition="start"
              label="Bubbles"
            />
          </Tabs>

          {activeTab === 0 && (
            <Tooltip title="Full screen feed">
              <IconButton size="small" onClick={() => setFeedDialogOpen(true)}
                sx={{ color: "text.secondary", p: 0.75, "&:hover": { color: "primary.main" } }}>
                <ExpandIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Minimize sidebar">
            <IconButton size="small" onClick={onToggleMinimize}
              sx={{ color: "text.secondary", p: 0.75, "&:hover": { color: "primary.main" } }}>
              <ChevronRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {activeTab === 0 && (
          <Box key={`idle-${idleKey}`}
            sx={{ position: "absolute", bottom: 0, left: 0, height: "1.5px", background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, opacity: 0.35, animation: `${idleFill} ${IDLE_MS}ms linear forwards` }}
          />
        )}
      </SidebarHeader>

      {activeTab === 0 && (
        <FeedScroll onScroll={handleInteraction}>
          {showFeatured && (
            <SidebarFeatured
              onCastingCallCreate={onCastingCallCreate}
              referralLink={referralLink} onCopyLink={copyLink}
              isGenerating={isGenRef} onDismiss={() => setShowFeatured(false)}
            />
          )}

          <ComposerBox sx={{ mt: showFeatured ? 1 : 0 }}>
            <TextField
              multiline minRows={2} maxRows={4} fullWidth
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What's happening?"
              variant="standard"
              InputProps={{ disableUnderline: true, sx: { fontSize: "0.84rem", lineHeight: 1.6 } }}
            />
            {uploadedMedia && (
              <Box sx={{ position: "relative", mt: 0.75 }}>
                <Box component="img" src={uploadedMedia.url}
                  sx={{ display: "block", width: "100%", maxHeight: 110, borderRadius: 1.5, objectFit: "cover", border: `1px solid ${alpha(theme.palette.divider, 0.4)}` }} />
                <IconButton size="small" onClick={() => setUploadedMedia(null)}
                  sx={{ position: "absolute", top: 5, right: 5, bgcolor: alpha(theme.palette.background.paper, 0.9), p: 0.3 }}>
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            )}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75 }}>
              <Box sx={{ display: "flex", gap: 0.25 }}>
                <Tooltip title="Add photo">
                  <span>
                    <IconButton size="small" onClick={() => setUploadDialogOpen(true)} disabled={!!uploadedMedia}
                      sx={{ color: "primary.main", p: 0.4 }}>
                      <ImageIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Casting call">
                  <IconButton size="small" onClick={onCastingCallCreate} sx={{ color: "secondary.main", p: 0.4 }}>
                    <CampaignIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {text.length > 200 && (
                  <Typography sx={{ fontSize: "0.6rem", alignSelf: "center", ml: 0.5, color: text.length > 280 ? "error.main" : "text.disabled" }}>
                    {text.length}/280
                  </Typography>
                )}
              </Box>
              <Button
                size="small" variant="contained" onClick={handlePost}
                disabled={(!text.trim() && !uploadedMedia) || text.length > 280 || createPost.isLoading}
                sx={{ borderRadius: 20, textTransform: "none", fontWeight: 700, fontSize: "0.72rem", px: 2, py: 0.35, minWidth: "auto" }}
              >
                {createPost.isLoading ? <CircularProgress size={11} sx={{ color: "inherit" }} /> : "Post"}
              </Button>
            </Box>
          </ComposerBox>

          <SectionDivider label="Latest" />

          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1.2, px: 1.5, py: 1.25, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.28)}` }}>
                  <Skeleton variant="circular" width={34} height={34} sx={{ flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", gap: 0.75, mb: 0.5 }}><Skeleton width="40%" height={13} /><Skeleton width="15%" height={13} /></Box>
                    <Skeleton width="95%" height={12} /><Skeleton width="80%" height={12} sx={{ mt: 0.3 }} />
                  </Box>
                </Box>
              ))
            : feed.map((act) => (
                <FeedItem
                  key={`${act.activity_type}-${act.activity_id ?? act.activity_date}`}
                  activity={act}
                  onCastingCallClick={handleCastingCallClick}
                  onCastingCallShareClick={handleCastingCallShareClick}
                  onShare={handleShare}
                  onReactionChange={() => {}}
                  userAuth={{ userOK, creatorOK }}
                  queryClient={qc}
                  onSnackbarMessage={(msg, sev) => setSnack({ msg, sev })}
                  isCreator={isCreator}
                  compact
                />
              ))}

          <Box ref={sentinelRef} sx={{ height: 2 }} />
          {isFetchingNextPage && <Box sx={{ py: 2, textAlign: "center" }}><CircularProgress size={16} thickness={4} /></Box>}

          <Box sx={{ py: 1.5, textAlign: "center", borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
            <Button size="small" variant="text" onClick={() => setFeedDialogOpen(true)}
              endIcon={<ExpandIcon sx={{ fontSize: 13 }} />}
              sx={{ textTransform: "none", fontSize: "0.72rem", color: "text.secondary", "&:hover": { color: "primary.main" } }}>
              Open full feed
            </Button>
          </Box>
          <Box sx={{ height: 12 }} />
        </FeedScroll>
      )}

      {activeTab === 1 && (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
          {wasAutoSwitched && (
            <AutoBanner>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <SparkleIcon sx={{ fontSize: 14, color: "primary.main" }} />
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 600 }}>Explore communities</Typography>
              </Box>
              <Button size="small" startIcon={<BackIcon sx={{ fontSize: 11 }} />}
                onClick={() => { setActiveTab(0); setWasAutoSwitched(false); }}
                sx={{ fontSize: "0.62rem", textTransform: "none", fontWeight: 700, py: 0.25, px: 1, borderRadius: 20, minWidth: "auto", color: "primary.main", border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}>
                Back to feed
              </Button>
            </AutoBanner>
          )}
          <FeedScroll onScroll={handleInteraction}><BubbleFeed embedded /></FeedScroll>
        </Box>
      )}

      <FullFeedDialog
        open={feedDialogOpen}
        onClose={() => setFeedDialogOpen(false)}
        onCastingCallCreate={onCastingCallCreate}
        referralLink={referralLink}
        onCopyReferralLink={copyLink}
        onViewReferrals={() => window.location.assign("/my-referrals")}
        isGeneratingReferral={isGenRef}
        displayName={displayName}
        bubbleCount={bubbleCount}
        photo={photo}
      />

      <CastingCallDialog open={castingCallViewOpen} onClose={() => { setCastingCallViewOpen(false); setSelectedCastingCall(null); setCastingCallPosterInfo(null); }} castingCall={selectedCastingCall} posterInfo={castingCallPosterInfo} />
      {shareActivity && <ThreadShareDialog open={shareDialogOpen} onClose={() => { setShareDialogOpen(false); setShareActivity(null); }} activity={shareActivity} />}
      <UploadFileDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} onUploadSuccess={(f) => { setUploadedMedia({ url: f.url, type: f.file_type === "gif" ? "gif" : "image" }); setSnack({ msg: "Image attached!", sev: "success" }); }} />
      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {snack && <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)}>{snack.msg}</Alert>}
      </Snackbar>
    </StickyWrapper>
  );
}

/* ─────────────────────────────────────────────────────────
   Mobile Layout
───────────────────────────────────────────────────────── */
const TAB_FOR_YOU = "for-you";
const TAB_BUBBLES = "bubbles";

function MobileLayout({ onCastingCallCreate }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthorized: userOK } = useAuth();
  const { isAuthorized: creatorOK } = useCreatorAuth();

  const getInitialTab = () => searchParams.get("tab") === TAB_BUBBLES ? 1 : 0;
  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    const p = searchParams.get("tab");
    const idx = p === TAB_BUBBLES ? 1 : 0;
    if (idx !== activeTab) setActiveTab(idx);
    if (!p) setSearchParams({ tab: TAB_FOR_YOU }, { replace: true });
  }, [searchParams]);

  const { data: bubblesData } = useQuery(["bubbles-list"], () => API.bubbles.list(), { enabled: userOK || creatorOK, staleTime: 60_000, refetchOnWindowFocus: false });
  const bubbleCount = bubblesData?.bubbles?.length || 0;

  const { referralLink, isLoading: isGenRef, copyLink } = useSidebarReferral();
  const handleCopyReferralLink = useCallback(async () => { await copyLink(); }, [copyLink]);

  const handleTabChange = (_, v) => {
    setActiveTab(v);
    setSearchParams({ tab: v === 1 ? TAB_BUBBLES : TAB_FOR_YOU }, { replace: true });
  };

  return (
    <MobileContainer>
      <MobileTabBar elevation={0}>
        <Tabs value={activeTab} onChange={handleTabChange}
          sx={{
            minHeight: 52,
            "& .MuiTabs-indicator": { height: 4, borderRadius: "4px 4px 0 0", background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})` },
            "& .MuiTabs-flexContainer": { justifyContent: "center" },
          }}
        >
          <MobileTab data-tour="for-you-tab" icon={<ExploreIcon />} iconPosition="start" label="For You" />
          <MobileTab
            data-tour="bubbles-tab"
            icon={
              <Badge color="primary" variant={bubbleCount > 0 ? "dot" : undefined} invisible={bubbleCount <= 0}
                sx={{ "& .MuiBadge-badge": { right: -4, top: 6, minWidth: 10, height: 10, borderRadius: "50%", boxShadow: `0 0 0 2px ${theme.palette.background.paper}` } }}>
                <GroupsIcon />
              </Badge>
            }
            iconPosition="start" label="Your Bubbles"
          />
        </Tabs>
      </MobileTabBar>

      <Box role="tabpanel" hidden={activeTab !== 0}>
        {activeTab === 0 && (
          <Box data-tour="activity-feed">
            <ZapFeed
              onCastingCallCreate={onCastingCallCreate}
              referralLink={referralLink}
              onCopyReferralLink={handleCopyReferralLink}
              onViewReferrals={() => navigate("/my-referrals")}
              isGeneratingReferral={isGenRef}
            />
          </Box>
        )}
      </Box>
      <Box role="tabpanel" hidden={activeTab !== 1}>
        {activeTab === 1 && <BubbleFeed />}
      </Box>
    </MobileContainer>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN DASHBOARD
───────────────────────────────────────────────────────── */
export default function Dashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { getCurrrentUser, isAuthorized: userOK } = useAuth();
  const { creatorToken, isAuthorized: creatorOK } = useCreatorAuth();

  const isCreator = !!creatorToken?.creator_user;

  // ── FIXED: guard getCurrrentUser() behind !isCreator.
  // Calling it for a creator session triggers GET /api/users/userinfo/{id},
  // which runs a SQL query joining on payouts.balance_credited_at — a column
  // that doesn't exist in the DB yet — returning a 500 for ALL creator requests.
  const userInfo = !isCreator ? getCurrrentUser() : null;

  const [sidebarMinimized, setSidebarMinimized] = useState(() => {
    try { return localStorage.getItem("blitz_sidebar_minimized") === "true"; }
    catch { return false; }
  });
  const handleToggleMinimize = () => {
    setSidebarMinimized((v) => {
      const next = !v;
      try { localStorage.setItem("blitz_sidebar_minimized", String(next)); } catch {}
      return next;
    });
  };
  const sidebarWidth = sidebarMinimized ? SIDEBAR_MINI_WIDTH : SIDEBAR_FULL_WIDTH;

  const { data: userSettings } = useQuery(
    ["userSettingsDashboard"],
    () => API.users.fetchUserSettings(),
    { enabled: userOK || creatorOK, staleTime: 1000 * 60 * 60 * 12, refetchOnWindowFocus: false }
  );
  const fontPref = userSettings?.font_preference || "default";
  const userFont = {
    roboto: '"Roboto","Helvetica","Arial",sans-serif',
    "open-sans": '"Open Sans",sans-serif',
    montserrat: '"Montserrat",sans-serif',
    default: "Inter, Arial, sans-serif",
  }[fontPref];

  // ── FIXED: userIdentifier never uses userInfo.id for creator sessions
  const userIdentifier = useMemo(() => {
    if (isCreator && creatorToken?.creator_user?.username) return creatorToken.creator_user.username;
    if (userOK && userInfo?.id) return userInfo.id;
    return null;
  }, [isCreator, creatorToken, userOK, userInfo]);

  const [celebrationAchievement, setCelebrationAchievement] = useState(null);
  const achievementStorageKey = userIdentifier ? `dashboard_seen_achievements_${userIdentifier}` : null;

  const { data: blitzData } = useQuery(
    ["blitzbolts-for-user", userIdentifier],
    () => API.blitzbolts.listForUser(userIdentifier, { scope: "self" }),
    { enabled: Boolean(userIdentifier), staleTime: 5 * 60 * 1000, refetchInterval: 30 * 1000, refetchOnWindowFocus: false }
  );

  const achievements = useMemo(() => {
    if (Array.isArray(blitzData)) return blitzData;
    if (blitzData?.bolts) return blitzData.bolts;
    if (blitzData?.data?.bolts) return blitzData.data.bolts;
    return [];
  }, [blitzData]);

  useEffect(() => {
    if (!achievements.length || !achievementStorageKey) return;
    let seen;
    try { seen = new Set(JSON.parse(localStorage.getItem(achievementStorageKey) || "[]")); }
    catch { seen = new Set(); }
    const newOnes = achievements.filter((a) => a?.attainment?.achieved && a.id && !seen.has(a.id));
    if (newOnes.length > 0) {
      const n = newOnes.sort((a, b) => new Date(b.attainment?.achieved_at || 0) - new Date(a.attainment?.achieved_at || 0))[0];
      setCelebrationAchievement({ id: n.id, title: n.title, description: n.description, icon_name: n.icon_name, points: n.points, achieved_at: n.attainment?.achieved_at });
      try { localStorage.setItem(achievementStorageKey, JSON.stringify(achievements.filter((a) => a?.attainment?.achieved).map((a) => a.id))); }
      catch {}
    }
  }, [achievements, achievementStorageKey]);

  const [castingCallDialogOpen, setCastingCallDialogOpen] = useState(false);
  const [snack, setSnack] = useState(null);

  const handleAchievementShare = (achievement) => {
    const t = `I just earned the "${achievement.title}" achievement worth ${achievement.points} points!`;
    if (navigator.share) navigator.share({ title: "Achievement Unlocked!", text: t, url: window.location.origin }).catch(() => {});
    else navigator.clipboard?.writeText(t).then(() => setSnack({ msg: "Copied!", sev: "success" }));
  };

  const handleCastingCallCreate = () => setCastingCallDialogOpen(true);

  return (
    <Box sx={{ fontFamily: (userOK || creatorOK) ? userFont : "inherit" }}>
      {isMobile && <MobileLayout onCastingCallCreate={handleCastingCallCreate} />}

      {!isMobile && (
        <PageContainer>
          <FinancialColumn>
            {isCreator ? <PayoutsInvoicing /> : <BlitzPay />}
          </FinancialColumn>
          <SidebarColumn sx={{ width: sidebarWidth }}>
            <SocialSidebar
              onCastingCallCreate={handleCastingCallCreate}
              minimized={sidebarMinimized}
              onToggleMinimize={handleToggleMinimize}
            />
          </SidebarColumn>
        </PageContainer>
      )}

      <AchievementCelebrationDialog
        open={Boolean(celebrationAchievement)}
        onClose={() => setCelebrationAchievement(null)}
        achievement={celebrationAchievement}
        onShare={handleAchievementShare}
      />

      <CreateCallDialog
        open={castingCallDialogOpen}
        handleClose={() => setCastingCallDialogOpen(false)}
        onSuccess={() => { setSnack({ msg: "Casting call created!", sev: "success" }); setCastingCallDialogOpen(false); }}
      />

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {snack && <Alert severity={snack.sev} variant="filled" onClose={() => setSnack(null)} sx={{ width: "100%" }}>{snack.msg}</Alert>}
      </Snackbar>
    </Box>
  );
}